import { openTodayNote } from "./navConfig";
import { useEffect, useState } from "react";
import { Redirect, Route, Switch, useLocation } from "wouter";
import { createNotebook, createTag, notebookNameError, tagNameError } from "../db/actions";
import { db } from "../db/client";
import { NotebooksPage } from "./NotebooksPage";
import { NotesPane } from "./NotesPane";
import { usePrefs } from "./prefs";
import { Splitter } from "./Splitter";
import { newNoteHere } from "./newNote";
import { Sidebar } from "./Sidebar";
import { TasksPage } from "./TasksPage";
import { FilesPage } from "./FilesPage";
import { HomePage } from "./HomePage";
import { useLayout } from "./layout";
import { Rail } from "./Rail";
import { TagsPanel } from "./TagsPanel";
import { Prompt } from "./Menu";
import { KeyboardHelp, QuickSwitcher } from "./Quick";
import { TaskDialogHost, openTaskDialog } from "./TaskDialog";
import { ColorThemePage, PaletteSync } from "./Appearance";
import { EditorWarmup } from "./editor/Warmup";
import { SettingsPage } from "./SettingsPage";
import { rememberBuild, UpdateBanner, VersionPage } from "./AppUpdates";
import { startEmptyNoteSweeps } from "./emptyNotes";

export function Shell({ onSignedOut }: { onSignedOut: () => void }) {
  const [tagsOpen, setTagsOpen] = useState(false);
  const collapsed = usePrefs((s) => s.sidebarCollapsed);
  const sidebarW = usePrefs((s) => s.sidebarWidth);
  const listW = usePrefs((s) => s.listWidth);
  const listH = usePrefs((s) => s.listHeight);
  const sizeVars = {
    ...(sidebarW ? { "--sidebar-w": `${sidebarW}px` } : {}),
    ...(listW ? { "--list-w": `${listW}px` } : {}),
    ...(listH ? { "--list-h": `${listH}px` } : {}),
  } as React.CSSProperties;
  const [path, navigate] = useLocation();
  const layout = useLayout();
  // The layout class also goes on <html>: dialogs and menus are portaled to <body>, outside .shell, and their
  // phone rules (".layout-narrow .modal …") must still apply.
  useEffect(() => {
    const html = document.documentElement;
    html.classList.add(`layout-${layout}`);
    return () => html.classList.remove(`layout-${layout}`);
  }, [layout]);
  // Like Evernote's flyout, the tags panel closes once something is picked.
  useEffect(() => {
    setTagsOpen(false);
  }, [path, layout]);

  const [dialog, setDialog] = useState<null | "quick" | "keys" | "notebook" | "tag">(null);
  // Empty notes left behind go away by themselves (emptyNotes.ts).
  useEffect(startEmptyNoteSweeps, []);
  useEffect(rememberBuild, []);

  useEffect(() => {
    const onKey = async (e: KeyboardEvent) => {
      if (e.defaultPrevented) return; // the editor or a dialog already handled it
      const k = e.key.toLowerCase();
      const ctrl = e.ctrlKey || e.metaKey;
      const run = (fn: () => unknown) => {
        e.preventDefault();
        void fn();
      };
      // Evernote shortcuts (help.evernote.com "Keyboard shortcuts"). Chrome keeps Ctrl+N, Ctrl+Shift+N and
      // Ctrl+Shift+T for itself, so those also have Alt variants; the Android app gets both.
      if ((ctrl && e.altKey && k === "n") || (ctrl && !e.altKey && !e.shiftKey && k === "n")) {
        run(() => newNoteHere(location.pathname, navigate)); // live path: this handler is set up once
      } else if (e.altKey && e.shiftKey && !ctrl && e.code === "KeyN") {
        run(() => setDialog("notebook"));
      } else if ((e.altKey && e.shiftKey && !ctrl && e.code === "KeyT") || (ctrl && e.shiftKey && !e.altKey && k === "t")) {
        run(() => setDialog("tag"));
      } else if (e.altKey && !e.shiftKey && !ctrl && e.code === "KeyT") {
        run(() => openTaskDialog());
      } else if (ctrl && e.altKey && !e.shiftKey && e.code === "KeyD") {
        run(() => openTodayNote(navigate));
      } else if (ctrl && !e.altKey && !e.shiftKey && k === "q") {
        run(() => setDialog("quick"));
      } else if (ctrl && !e.altKey && !e.shiftKey && e.code === "Comma") {
        run(() => navigate("/settings")); // Evernote: Ctrl+, opens Settings
      } else if (ctrl && !e.altKey && (k === "/" || e.code === "Slash")) {
        run(() => setDialog("keys"));
      } else if (ctrl && e.altKey && k === "f") {
        run(() => setDialog("quick"));
      } else if (ctrl && e.altKey && !e.shiftKey && ["Digit1", "Digit2", "Digit3"].includes(e.code)) {
        // In the editor these are headings (handled above via defaultPrevented).
        run(() => (e.code === "Digit1" ? navigate("/notes") : e.code === "Digit2" ? navigate("/notebooks") : setTagsOpen((v) => !v)));
      } else if (ctrl && e.altKey && e.code === "Backslash") {
        run(() => document.querySelector<HTMLElement>(".note-card.selected, .top-table tr.selected, .note-card, .top-table tbody tr")?.focus());
      } else if (ctrl && !e.altKey && !e.shiftKey && (e.code === "BracketLeft" || e.code === "BracketRight")) {
        run(() => (e.code === "BracketLeft" ? history.back() : history.forward()));
      } else if (e.key === "F10" && !ctrl && !e.altKey) {
        run(() => usePrefs.getState().set({ sidebarCollapsed: !usePrefs.getState().sidebarCollapsed }));
      } else if (ctrl && !e.altKey && !e.shiftKey && /^[1-9]$/.test(e.key)) {
        // Ctrl+1..9 open shortcuts in order. Prevent first: the lookup is async.
        e.preventDefault();
        const rows = await db.query<{ kind: string; target_id: string }>("SELECT kind, target_id FROM shortcuts WHERE deleted = 0 ORDER BY position");
        const s = rows[Number(e.key) - 1];
        if (s) navigate(shortcutHref(s.kind, s.target_id));
      }
    };
    const openSearch = () => setDialog("quick");
    window.addEventListener("keydown", onKey);
    window.addEventListener("engram:search", openSearch);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("engram:search", openSearch);
    };
  }, [navigate]);

  return (
    <div className={`shell layout-${layout}${collapsed && layout === "wide" ? " sidebar-collapsed" : ""}`} style={sizeVars}>
      <PaletteSync /> {/* the colour theme picked on another device */}
      {layout === "narrow" && <EditorWarmup />}
      {layout === "wide" ? (
        <>
          <Sidebar tagsOpen={tagsOpen} onToggleTags={() => setTagsOpen((v) => !v)} onSignedOut={onSignedOut} />
          {!collapsed && <Splitter cssVar="--sidebar-w" min={180} max={() => 420} onCommit={(px) => usePrefs.getState().set({ sidebarWidth: px })} />}
        </>
      ) : (
        <Rail tagsOpen={tagsOpen} onToggleTags={() => setTagsOpen((v) => !v)} onSignedOut={onSignedOut} />
      )}
      {tagsOpen && <TagsPanel onClose={() => setTagsOpen(false)} />}
      {dialog === "quick" && <QuickSwitcher onClose={() => setDialog(null)} />}
      {dialog === "keys" && <KeyboardHelp onClose={() => setDialog(null)} />}
      <TaskDialogHost />
      <UpdateBanner />
      {dialog === "notebook" && (
        <Prompt title="New notebook" label="Name" confirm="Create" validate={notebookNameError("notebook")} onSubmit={async (v) => navigate(`/notebook/${await createNotebook(v)}`)} onClose={() => setDialog(null)} />
      )}
      {dialog === "tag" && <Prompt title="New tag" label="Name" confirm="Create" validate={tagNameError()} onSubmit={async (v) => navigate(`/tag/${await createTag(v)}`)} onClose={() => setDialog(null)} />}
      <main className="main">
        <Switch>
          <Route path="/">
            <Redirect to="/home" />
          </Route>
          <Route path="/home">
            <HomePage />
          </Route>
          <Route path="/notebooks">
            <NotebooksPage />
          </Route>
          <Route path="/tasks">
            <TasksPage />
          </Route>
          <Route path="/files">
            <FilesPage />
          </Route>
          <Route path="/settings">
            <SettingsPage />
          </Route>
          <Route path="/settings/theme">
            <ColorThemePage />
          </Route>
          <Route path="/settings/version">
            <VersionPage />
          </Route>
          <Route path="/note/:id">{(p) => <NoteRedirect id={p.id} />}</Route>
          <Route>
            <NotesPane />
          </Route>
        </Switch>
      </main>
    </div>
  );
}

export function shortcutHref(kind: string, id: string): string {
  switch (kind) {
    case "note":
      return `/shortcuts?n=${id}`;
    case "notebook":
      return `/notebook/${id}`;
    case "stack":
      return `/stack/${id}`;
    case "tag":
      return `/tag/${id}`;
    default:
      return `/search?saved=${id}`;
  }
}

/** Deep link /note/:id → open it inside its notebook. */
function NoteRedirect({ id }: { id: string }) {
  const [to, setTo] = useState<string | null>(null);
  useEffect(() => {
    void db.get<{ notebook_id: string; trashed_at: number | null }>("SELECT notebook_id, trashed_at FROM notes WHERE id = ? AND deleted = 0", [id]).then((n) => {
      if (!n) setTo("/notes");
      else setTo(n.trashed_at ? `/trash?n=${id}` : `/notebook/${n.notebook_id}?n=${id}`);
    });
  }, [id]);
  return to ? <Redirect to={to} replace /> : null;
}
